import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import ImageProfil from '../Profil/ImageProfil';

const COLORS = ['#f59e0b', '#10b981', '#4f46e5', '#ef4444', '#8b5cf6', '#ec4899'];
const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

const ChatbotStats = ({ stats }) => {
  if (!stats || !stats.details) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px', color: '#6b7280' }}>
        <i className="fa-solid fa-spinner fa-spin fa-2x"></i>
        <span style={{ marginLeft: '10px', fontSize: '1.2rem' }}>Chargement des statistiques...</span>
      </div>
    );
  }

  const {
    questionsPerDay,
    questionsPerMonth,
    agentsQuestions,
    themes,
    percentUnanswered,
    newAnswersMonth,
    contributorsMonth,
    totalQuestionsMonth
  } = stats.details;

  // Préparation des données pour les graphiques
  const themesData = themes.map(t => ({
    name: t.category || "Général",
    value: parseInt(t.count)
  })).sort((a, b) => b.value - a.value);

  const unansweredData = [
    { name: 'Répondues', value: 100 - parseFloat(percentUnanswered) },
    { name: 'Sans réponse', value: parseFloat(percentUnanswered) }
  ];

  const StatCard = ({ title, value, icon, color, subtext }) => (
    <div style={styles.kpiCard}>
        <div style={{ ...styles.iconContainer, backgroundColor: `${color}20`, color: color }}>
            <i className={icon} style={{ fontSize: '1.5rem' }}></i>
        </div>
        <div>
            <div style={styles.kpiTitle}>{title}</div>
            <div style={styles.kpiValue}>{value}</div>
            {subtext && <div style={styles.kpiSubtext}>{subtext}</div>}
        </div>
    </div>
  );

  return (
    <div className="stats-dashboard" style={styles.dashboardContainer}>
      
      {/* KPI Section */}
      <div style={styles.kpiGrid}>
        <StatCard 
            title="Questions (Mois)" 
            value={totalQuestionsMonth} 
            icon="fa-regular fa-comments" 
            color="#f59e0b"
            subtext="Interactions totales"
        />
        <StatCard 
            title="Taux de Réponse" 
            value={`${(100 - percentUnanswered).toFixed(1)}%`} 
            icon="fa-solid fa-check-double" 
            color="#10b981"
            subtext={`${percentUnanswered}% sans réponse`}
        />
        <StatCard 
            title="Nouvelles Réponses" 
            value={newAnswersMonth} 
            icon="fa-solid fa-plus-circle" 
            color="#4f46e5"
            subtext="Ajoutées ce mois-ci"
        />
         <StatCard 
            title="Contributeurs" 
            value={contributorsMonth.length} 
            icon="fa-solid fa-users-cog" 
            color="#8b5cf6"
            subtext="Actifs ce mois-ci"
        />
      </div>

      {/* Charts Grid */}
      <div style={styles.chartsGrid}>
        
        {/* Main Line Chart - Full Width */}
        <div style={{ ...styles.chartCard, gridColumn: '1 / -1' }}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Évolution des questions (30 derniers jours)</h3>
            <i className="fa-solid fa-chart-line" style={{ color: '#9ca3af' }}></i>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={questionsPerDay} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#404040" />
              <XAxis 
                dataKey="date" 
                tick={{fill: '#9ca3af', fontSize: 12}} 
                tickLine={false}
                axisLine={{stroke: '#404040'}}
                tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'})}
              />
              <YAxis 
                tick={{fill: '#9ca3af', fontSize: 12}} 
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)', color: '#fff' }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }}/>
              <Line 
                type="monotone" 
                dataKey="count" 
                name="Questions posées" 
                stroke="#f59e0b" 
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 8 }} 
              />
              <Line 
                type="monotone" 
                dataKey="answered_count" 
                name="Réponses auto (Bot)" 
                stroke="#10b981" 
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 8 }} 
              />
              <Line 
                type="monotone" 
                dataKey="admin_count" 
                name="Réponses (Admin)" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Historique Mensuel</h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={questionsPerMonth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#404040" />
              <XAxis dataKey="month" tick={{fill: '#9ca3af', fontSize: 12}} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)', color: '#fff' }}/>
              <Bar dataKey="count" name="Questions" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart - Themes */}
        <div style={styles.chartCard}>
           <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Répartition par Thématique</h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={themesData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {themesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)', color: '#f59e0b' }} />
              <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '12px' }} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Horizontal Bar Chart - Top Users */}
         <div style={styles.chartCard}>
           <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Utilisateurs les plus actifs (Top 10)</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={agentsQuestions} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#404040" />
              <XAxis type="number" hide />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={150} 
                tick={{fill: '#9ca3af', fontSize: 13, fontWeight: 500}} 
                tickLine={false}
                axisLine={false}
              />
              <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)', color: '#fff' }}/>
              <Bar dataKey="count" name="Questions posées" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20}>
                {agentsQuestions.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Contributors Table */}
      <div style={styles.chartCard}>
         <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Top Contributeurs (Création de réponses)</h3>
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Ce mois-ci</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Collaborateur</th>
                        <th style={styles.th}>Actions</th>
                        <th style={styles.th}>Volume</th>
                    </tr>
                </thead>
                <tbody>
                    {contributorsMonth.length > 0 ? contributorsMonth.map((user, idx) => (
                        <tr key={idx} style={styles.tr}>
                            <td style={styles.td}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {/* Avatar Logic */}
                                    {user.userId ? (
                                         <div style={{ 
                                            width: '32px', 
                                            height: '32px', 
                                            borderRadius: '50%', 
                                            overflow: 'hidden', 
                                            marginRight: '12px',
                                            flexShrink: 0
                                         }}>
                                            <ImageProfil 
                                                id={user?.userId} 
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                         </div>
                                    ) : (
                                        <div style={styles.avatarPlaceholder}>
                                            {(user.firstname && user.name) ? `${user.firstname.charAt(0)}${user.name.charAt(0)}` : "?"}
                                        </div>
                                    )}
                                    
                                    <span style={{ fontWeight: 500, color: '#e5e7eb' }}>
                                        {user.firstname && user.name ? `${user.firstname} ${user.name}` : "Utilisateur Inconnu"}
                                    </span>
                                </div>
                            </td>
                            <td style={styles.td}>
                                <span style={styles.badge}>Ajout de réponses</span>
                            </td>
                            <td style={styles.td}>
                                <strong style={{ color: '#818cf8' }}>{user.count}</strong>
                            </td>
                        </tr>
                    )) : (
                        <tr><td colSpan="3" style={{ ...styles.td, textAlign: 'center', color: '#6b7280' }}>Aucune contribution enregistrée ce mois-ci.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

const styles = {
  dashboardContainer: {
    padding: '20px 0',
    backgroundColor: 'transparent',
    fontFamily: "'Inter', sans-serif"
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  },
  kpiCard: {
    backgroundColor: '#2d2d2d',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'transform 0.2s',
    cursor: 'default',
    border: '1px solid #404040'
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  kpiTitle: {
    fontSize: '0.875rem',
    color: '#9ca3af',
    fontWeight: '500',
    marginBottom: '4px'
  },
  kpiValue: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: '1.2'
  },
  kpiSubtext: {
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '2px'
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    marginBottom: '24px'
  },
  chartCard: {
    backgroundColor: '#2d2d2d',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)',
    marginBottom: '20px',
    border: '1px solid #404040'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  cardTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0'
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #404040'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #404040',
    fontSize: '0.875rem',
    color: '#e5e7eb'
  },
  tr: {
    transition: 'background-color 0.15s'
  },
  avatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#374151',
    color: '#818cf8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: '600',
    marginRight: '12px'
  },
  badge: {
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    color: '#34d399',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500'
  }
};

export default ChatbotStats;
